import { type ReactElement } from 'react'
import { type GameRelevanceGroup, type GameViewerRelation, type RelevantGameData } from '@terros/common'
import { Badge, Group, Paper, Stack, Text } from '@mantine/core'
import { toReadableCompetitionType, useTranslation, type TranslateFunction } from '@front/model'
import { MaterialSymbolIcon } from '@components'
import { toCompetitionTiming } from './competitionTiming'

type Props = {
  item: RelevantGameData
  now: number
  onOpen: (item: RelevantGameData) => void
}

function toGroupBadge(group: GameRelevanceGroup, t: TranslateFunction): { label: string; color: string } {
  switch (group) {
    case 'current':
      return { label: t('Live'), color: 'green' }
    case 'recentlyEnded':
    case 'earlier':
      return { label: t('Ended'), color: 'neutral' }
    case 'upcomingSoon':
    case 'upcomingLater':
      return { label: t('Soon'), color: 'blue' }
  }
}

function toRelationLabel(relation: GameViewerRelation, t: TranslateFunction): string {
  switch (relation) {
    case 'participant':
      return t('Competing')
    case 'creator':
      return t('Created by you')
    case 'permission':
      return t('Created by a teammate')
  }
}

/**
 * A competition as a banner with its title. Everyone gets the same banner for now: a creator who is not competing
 * sees no more than a participant does, and raffles have no layout of their own yet.
 */
export function CompetitionWidgetRow({ item, now, onOpen }: Props): ReactElement {
  const { t } = useTranslation()
  const { game, group, relation } = item
  const badge = toGroupBadge(group, t)
  const details = [
    toReadableCompetitionType(game.gameType),
    toCompetitionTiming({ game, group, now, t }),
    toRelationLabel(relation, t),
  ]

  return (
    <Paper withBorder component='button' p='sm' radius='md' type='button' onClick={() => onOpen(item)}>
      <Group gap='sm' justify='space-between' wrap='nowrap'>
        <Group gap='sm' wrap='nowrap' className='min-w-0'>
          <MaterialSymbolIcon icon='trophy' size='md' />
          <Stack gap={2} className='min-w-0 text-start'>
            <Text className='font-semibold' lineClamp={1}>
              {game.name}
            </Text>
            <Text c='dimmed' lineClamp={1} size='xs'>
              {details.join(' · ')}
            </Text>
          </Stack>
        </Group>
        <Badge color={badge.color} size='sm' variant='light'>
          {badge.label}
        </Badge>
      </Group>
    </Paper>
  )
}
